import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatCurrency, formatDate, formatDateTime } from '../lib/utils';
import axios from 'axios';
import { Plus, Search, Receipt, Printer, Eye, Trash2, MessageCircle, FileText, Pencil } from 'lucide-react';
import ExportButton from '../components/ExportButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SalesPage() {
  const { t, language } = useLanguage();
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [summary, setSummary] = useState({
    total_count: 0,
    total_revenue: 0,
    total_unpaid: 0,
    paid_count: 0,
    partial_count: 0,
    pending_count: 0,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [statementDialogOpen, setStatementDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  const [selectedCustomerForStatement, setSelectedCustomerForStatement] = useState(null);
  const [statementSales, setStatementSales] = useState([]);
  const printRef = useRef();
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: 'Walk-in Customer',
    items: [],
    payment_status: 'pending',
    paid_amount: 0,
    discount: 0,
    delivery_fee: 0,
    payment_method: 'cash',
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [quickMode, setQuickMode] = useState(false);
  const [quickAmount, setQuickAmount] = useState('');
  const [newItem, setNewItem] = useState({ product_id: '', quantity: '' });
  const [statementData, setStatementData] = useState({
    customer_id: '',
    start_date: '',
    end_date: '',
    type: 'summary', // 'summary' or 'detailed'
  });

  const fetchData = useCallback(async () => {
    try {
      const [salesRes, customersRes, productsRes] = await Promise.allSettled([
        axios.get(`${API}/sales/paginated`, {
          params: {
            skip: page * PAGE_SIZE,
            limit: PAGE_SIZE,
            search,
            payment_status: statusFilter,
          },
        }),
        axios.get(`${API}/customers`),
        axios.get(`${API}/products`),
      ]);
      if (salesRes.status === 'fulfilled') {
        setSales(salesRes.value.data.items || []);
        setFilteredTotal(salesRes.value.data.total || 0);
        setSummary(salesRes.value.data.summary || summary);
      }
      if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.data);
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounce search input → reset to first page when user types
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Reset to first page when status filter changes
  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  // Load all sales for the selected customer when the statement dialog is active
  // so statements still work with server-side pagination on the main list.
  useEffect(() => {
    let cancelled = false;
    if (!statementDialogOpen || !statementData.customer_id) {
      setStatementSales([]);
      return;
    }
    axios
      .get(`${API}/sales/by-customer`, {
        params: { customer_id: statementData.customer_id },
      })
      .then((res) => {
        if (!cancelled) setStatementSales(res.data || []);
      })
      .catch((err) => {
        console.error('Error loading customer sales for statement:', err);
        if (!cancelled) setStatementSales([]);
      });
    return () => {
      cancelled = true;
    };
  }, [statementDialogOpen, statementData.customer_id]);

  const handleAddItem = () => {
    const product = products.find((p) => p.id === newItem.product_id);
    if (product && newItem.quantity) {
      setFormData({
        ...formData,
        items: [
          ...formData.items,
          {
            product_id: product.id,
            product_name: product.name, // Always use English name
            quantity: parseFloat(newItem.quantity),
            unit_price: product.price,
          },
        ],
      });
      setNewItem({ product_id: '', quantity: '' });
    }
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const handleCustomerChange = (customerId) => {
    if (customerId === 'walk-in') {
      setFormData({ ...formData, customer_id: '', customer_name: 'Walk-in Customer' });
    } else {
      const customer = customers.find((c) => c.id === customerId);
      setFormData({ ...formData, customer_id: customerId, customer_name: customer?.name || '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let saleData;
      if (quickMode && !editingSale) {
        const amount = parseFloat(quickAmount || 0);
        saleData = {
          ...formData,
          items: [{ product_id: 'quick', product_name: language === 'ar' ? 'دفعة مباشرة' : 'Direct Payment', quantity: 1, unit_price: amount }],
          discount: 0,
          delivery_fee: parseFloat(formData.delivery_fee || 0),
          paid_amount: formData.payment_status === 'paid' ? amount + parseFloat(formData.delivery_fee || 0) : parseFloat(formData.paid_amount || 0),
        };
      } else {
        saleData = {
          ...formData,
          discount: parseFloat(formData.discount || 0),
          delivery_fee: parseFloat(formData.delivery_fee || 0),
          paid_amount: formData.payment_status === 'paid' ? calculateTotal() : parseFloat(formData.paid_amount || 0),
        };
      }
      if (editingSale) {
        await axios.put(`${API}/sales/${editingSale.id}`, saleData);
      } else {
        await axios.post(`${API}/sales`, saleData);
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving sale:', error);
    }
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setFormData({
      customer_id: sale.customer_id || '',
      customer_name: sale.customer_name || '',
      items: sale.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
      })),
      payment_status: sale.payment_status || 'pending',
      paid_amount: sale.paid_amount || 0,
      discount: sale.discount || 0,
      delivery_fee: sale.delivery_fee || 0,
      payment_method: sale.payment_method || 'cash',
      notes: sale.notes || '',
      date: sale.date ? sale.date.split('T')[0] : new Date().toISOString().split('T')[0],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this sale?')) {
      try {
        await axios.delete(`${API}/sales/${id}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting sale:', error);
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSale(null);
    setQuickMode(false);
    setQuickAmount('');
    setFormData({
      customer_id: '',
      customer_name: 'Walk-in Customer',
      items: [],
      payment_status: 'pending',
      paid_amount: 0,
      discount: 0,
      delivery_fee: 0,
      payment_method: 'cash',
      notes: '',
      date: new Date().toISOString().split('T')[0],
    });
    setNewItem({ product_id: '', quantity: '' });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() - parseFloat(formData.discount || 0) + parseFloat(formData.delivery_fee || 0);
  };

  // Company Information
  const companyInfo = {
    name: 'Qassion Bakeries and Sweets',
    phone: '+44 7485 078756',
    email: 'info@qassioun-bakeries.co.uk',
    address: 'Unit 19, Pop in business, Commercial centre, HA9 0HF'
  };

  // Generate English Invoice HTML
  const generateInvoiceHTML = (sale) => {
    const itemsHTML = sale.items.map((item, index) => `
      <tr>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">${index + 1}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee;">${item.product_name}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">${item.quantity}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">£${item.unit_price.toFixed(2)}</td>
        <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">£${(item.quantity * item.unit_price).toFixed(2)}</td>
      </tr>
    `).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Invoice ${sale.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; }
          .invoice-container { max-width: 600px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #C2185B; padding-bottom: 15px; }
          .logo { width: 70px; height: 70px; margin-bottom: 8px; }
          .company-name { font-size: 24px; font-weight: bold; color: #C2185B; margin: 0; }
          .company-info { font-size: 12px; color: #666; margin-top: 8px; line-height: 1.6; }
          .company-info a { color: #C2185B; text-decoration: none; }
          .invoice-title { font-size: 24px; color: #333; margin: 20px 0; text-align: center; }
          .invoice-details { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .invoice-details div { flex: 1; }
          .label { font-weight: bold; color: #666; font-size: 12px; text-transform: uppercase; }
          .value { font-size: 14px; margin-top: 4px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th { background: #C2185B; color: white; padding: 12px 10px; text-align: left; }
          th:nth-child(3), th:nth-child(4), th:nth-child(5) { text-align: right; }
          .totals { margin-top: 20px; text-align: right; }
          .totals-row { display: flex; justify-content: flex-end; margin: 8px 0; }
          .totals-label { width: 150px; text-align: right; padding-right: 20px; }
          .totals-value { width: 100px; text-align: right; font-weight: bold; }
          .grand-total { font-size: 20px; color: #C2185B; border-top: 2px solid #C2185B; padding-top: 10px; margin-top: 10px; }
          .footer { text-align: center; margin-top: 30px; padding-top: 15px; border-top: 1px solid #eee; color: #666; font-size: 11px; }
          .footer-contact { margin-top: 10px; font-size: 11px; }
          .payment-status { display: inline-block; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: bold; }
          .status-paid { background: #d4edda; color: #155724; }
          .status-pending { background: #fff3cd; color: #856404; }
          .status-partial { background: #ffe5d0; color: #8a4500; }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_877cf3ae-79e1-4b85-af95-e3f38fcbc2fb/artifacts/imx8202j_909ff97a-29cc-4bc8-9a25-59a8865473f7-300x294.png" alt="Logo" class="logo">
            <h1 class="company-name">${companyInfo.name}</h1>
            <div class="company-info">
              📍 ${companyInfo.address}<br>
              📞 ${companyInfo.phone} | ✉️ <a href="mailto:${companyInfo.email}">${companyInfo.email}</a>
            </div>
          </div>
          
          <h2 class="invoice-title">INVOICE</h2>
          
          <div class="invoice-details">
            <div>
              <p class="label">Invoice Number</p>
              <p class="value">${sale.invoice_number}</p>
              <p class="label" style="margin-top: 15px;">Date</p>
              <p class="value">${new Date(sale.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            </div>
            <div style="text-align: right;">
              <p class="label">Bill To</p>
              <p class="value">${sale.customer_name}</p>
              <p class="label" style="margin-top: 15px;">Status</p>
              <p class="value">
                <span class="payment-status status-${sale.payment_status}">${sale.payment_status.toUpperCase()}</span>
              </p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th style="width: 40px;">#</th>
                <th>Item Description</th>
                <th style="width: 80px; text-align: center;">Qty</th>
                <th style="width: 100px; text-align: right;">Unit Price</th>
                <th style="width: 100px; text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHTML}
            </tbody>
          </table>
          
          <div class="totals">
            <div class="totals-row">
              <span class="totals-label">Subtotal:</span>
              <span class="totals-value">£${sale.subtotal.toFixed(2)}</span>
            </div>
            ${sale.discount > 0 ? `
            <div class="totals-row" style="color: #28a745;">
              <span class="totals-label">Discount:</span>
              <span class="totals-value">-£${sale.discount.toFixed(2)}</span>
            </div>
            ` : ''}
            ${(sale.delivery_fee || 0) > 0 ? `
            <div class="totals-row">
              <span class="totals-label">Delivery Fee:</span>
              <span class="totals-value">£${sale.delivery_fee.toFixed(2)}</span>
            </div>
            ` : ''}
            <div class="totals-row grand-total">
              <span class="totals-label">Total:</span>
              <span class="totals-value">£${sale.total.toFixed(2)}</span>
            </div>
            ${sale.payment_status !== 'paid' ? `
            <div class="totals-row" style="color: #28a745;">
              <span class="totals-label">Paid:</span>
              <span class="totals-value">£${sale.paid_amount.toFixed(2)}</span>
            </div>
            <div class="totals-row" style="color: #dc3545; font-weight: bold;">
              <span class="totals-label">Remaining Balance:</span>
              <span class="totals-value">£${(sale.total - sale.paid_amount).toFixed(2)}</span>
            </div>
            ` : ''}
          </div>
          
          <div class="footer">
            <p><strong>Thank you for your business!</strong></p>
            <div class="footer-contact">
              ${companyInfo.name}<br>
              ${companyInfo.address}<br>
              Tel: ${companyInfo.phone} | Email: ${companyInfo.email}
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handlePrint = () => {
    if (!selectedSale) return;
    const htmlContent = generateInvoiceHTML(selectedSale);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url);
    printWindow.onload = () => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
        URL.revokeObjectURL(url);
      }, 500);
    };
  };

  // Send invoice via WhatsApp
  const handleWhatsAppSend = (sale) => {
    const customer = customers.find(c => c.id === sale.customer_id);
    let phone = customer?.phone || '';
    
    // Clean phone number (remove spaces, dashes, etc.)
    phone = phone.replace(/[\s\-\(\)]/g, '');
    if (!phone.startsWith('+')) {
      phone = '+44' + phone.replace(/^0/, ''); // Default to UK
    }
    phone = phone.replace('+', '');

    // Create message
    const message = `
*INVOICE - ${companyInfo.name}*
━━━━━━━━━━━━━━━━━━━━
Invoice: ${sale.invoice_number}
Date: ${new Date(sale.date).toLocaleDateString('en-GB')}
Customer: ${sale.customer_name}
━━━━━━━━━━━━━━━━━━━━

*Items:*
${sale.items.map((item, i) => `${i + 1}. ${item.product_name} x${item.quantity} = £${(item.quantity * item.unit_price).toFixed(2)}`).join('\n')}

━━━━━━━━━━━━━━━━━━━━
Subtotal: £${sale.subtotal.toFixed(2)}
${sale.discount > 0 ? `Discount: -£${sale.discount.toFixed(2)}\n` : ''}${(sale.delivery_fee || 0) > 0 ? `Delivery Fee: £${sale.delivery_fee.toFixed(2)}\n` : ''}*TOTAL: £${sale.total.toFixed(2)}*
${sale.payment_status !== 'paid' ? `Paid: £${sale.paid_amount.toFixed(2)}\n*REMAINING: £${(sale.total - sale.paid_amount).toFixed(2)}*\n` : ''}Status: ${sale.payment_status.toUpperCase()}
━━━━━━━━━━━━━━━━━━━━

Thank you for your business!

_${companyInfo.name}_
📍 ${companyInfo.address}
📞 ${companyInfo.phone}
✉️ ${companyInfo.email}
    `.trim();

    // Open WhatsApp with pre-filled message
    const whatsappUrl = phone 
      ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    
    // Try to open in new tab, fallback to same window
    const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // Popup was blocked, redirect in same window
      window.location.href = whatsappUrl;
    }
  };

  const viewInvoice = async (sale) => {
    setSelectedSale(sale);
    setInvoiceDialogOpen(true);
  };

  // Client Statement Functions
  const openStatementDialog = (customer = null) => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    
    setStatementData({
      customer_id: customer?.id || '',
      start_date: firstDayOfMonth.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0],
      type: 'summary',
    });
    setSelectedCustomerForStatement(customer);
    setStatementDialogOpen(true);
  };

  const generateStatementHTML = () => {
    const customer = customers.find(c => c.id === statementData.customer_id);
    if (!customer) return '';

    const filteredSales = statementSales.filter(sale => {
      const saleDateStr = sale.date.split('T')[0];
      return saleDateStr >= statementData.start_date && saleDateStr <= statementData.end_date;
    });

    const totalAmount = filteredSales.reduce((sum, sale) => sum + sale.total, 0);
    const totalPaid = filteredSales.reduce((sum, sale) => sum + (sale.paid_amount || (sale.payment_status === 'paid' ? sale.total : 0)), 0);
    const totalDue = totalAmount - totalPaid;

    let contentHTML = '';

    if (statementData.type === 'summary') {
      contentHTML = `
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Invoice #</th>
              <th style="text-align: right;">Amount</th>
              <th style="text-align: right;">Paid</th>
              <th style="text-align: center;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${filteredSales.map(sale => `
              <tr>
                <td style="padding: 10px; border-bottom: 1px solid #eee;">${new Date(sale.date).toLocaleDateString('en-GB')}</td>
                <td style="padding: 10px; border-bottom: 1px solid #eee;">${sale.invoice_number}</td>
                <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">£${sale.total.toFixed(2)}</td>
                <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: right;">£${(sale.paid_amount || (sale.payment_status === 'paid' ? sale.total : 0)).toFixed(2)}</td>
                <td style="padding: 10px; border-bottom: 1px solid #eee; text-align: center;">
                  <span style="padding: 3px 10px; border-radius: 10px; font-size: 11px; background: ${sale.payment_status === 'paid' ? '#d4edda' : '#fff3cd'}; color: ${sale.payment_status === 'paid' ? '#155724' : '#856404'};">
                    ${sale.payment_status.toUpperCase()}
                  </span>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    } else {
      // Detailed view
      contentHTML = filteredSales.map(sale => `
        <div style="margin-bottom: 30px; padding: 15px; border: 1px solid #eee; border-radius: 8px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #eee;">
            <div>
              <strong>${sale.invoice_number}</strong><br>
              <span style="color: #666; font-size: 12px;">${new Date(sale.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            </div>
            <div style="text-align: right;">
              <span style="padding: 3px 10px; border-radius: 10px; font-size: 11px; background: ${sale.payment_status === 'paid' ? '#d4edda' : '#fff3cd'}; color: ${sale.payment_status === 'paid' ? '#155724' : '#856404'};">
                ${sale.payment_status.toUpperCase()}
              </span>
            </div>
          </div>
          <table style="width: 100%; margin-bottom: 10px;">
            <thead>
              <tr style="background: #f8f9fa;">
                <th style="padding: 8px; text-align: left;">Item</th>
                <th style="padding: 8px; text-align: center;">Qty</th>
                <th style="padding: 8px; text-align: right;">Price</th>
                <th style="padding: 8px; text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${sale.items.map(item => `
                <tr>
                  <td style="padding: 8px;">${item.product_name}</td>
                  <td style="padding: 8px; text-align: center;">${item.quantity}</td>
                  <td style="padding: 8px; text-align: right;">£${item.unit_price.toFixed(2)}</td>
                  <td style="padding: 8px; text-align: right;">£${(item.quantity * item.unit_price).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div style="text-align: right; font-weight: bold; color: #C2185B;">
            Total: £${sale.total.toFixed(2)}
          </div>
        </div>
      `).join('');
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Statement - ${customer.name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; }
          .container { max-width: 800px; margin: 0 auto; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 3px solid #C2185B; padding-bottom: 15px; }
          .logo { width: 60px; height: 60px; margin-bottom: 8px; }
          .company-name { font-size: 22px; font-weight: bold; color: #C2185B; margin: 0; }
          .company-info { font-size: 11px; color: #666; margin-top: 8px; line-height: 1.5; }
          .statement-title { font-size: 20px; color: #333; margin: 20px 0; text-align: center; background: #f8f9fa; padding: 10px; border-radius: 8px; }
          .customer-info { display: flex; justify-content: space-between; margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #C2185B; color: white; padding: 12px 10px; text-align: left; }
          .summary-box { margin-top: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px; }
          .summary-row { display: flex; justify-content: space-between; padding: 8px 0; }
          .summary-total { font-size: 18px; font-weight: bold; color: #C2185B; border-top: 2px solid #C2185B; margin-top: 10px; padding-top: 10px; }
          .footer { text-align: center; margin-top: 30px; color: #666; font-size: 11px; padding-top: 15px; border-top: 1px solid #eee; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_877cf3ae-79e1-4b85-af95-e3f38fcbc2fb/artifacts/imx8202j_909ff97a-29cc-4bc8-9a25-59a8865473f7-300x294.png" alt="Logo" class="logo">
            <h1 class="company-name">${companyInfo.name}</h1>
            <div class="company-info">
              📍 ${companyInfo.address}<br>
              📞 ${companyInfo.phone} | ✉️ ${companyInfo.email}
            </div>
          </div>
          
          <div class="statement-title">
            ACCOUNT STATEMENT
          </div>
          
          <div class="customer-info">
            <div>
              <strong>Customer:</strong> ${customer.name}<br>
              <span style="color: #666;">Phone: ${customer.phone || 'N/A'}</span>
              ${customer.address ? `<br><span style="color: #666;">Address: ${customer.address}</span>` : ''}
            </div>
            <div style="text-align: right;">
              <strong>Period:</strong><br>
              ${statementData.start_date.split('-').reverse().join('/')} - ${statementData.end_date.split('-').reverse().join('/')}
            </div>
          </div>
          
          ${contentHTML}
          
          <div class="summary-box">
            <div class="summary-row">
              <span>Total Invoices:</span>
              <span>${filteredSales.length}</span>
            </div>
            <div class="summary-row">
              <span>Total Amount:</span>
              <span>£${totalAmount.toFixed(2)}</span>
            </div>
            <div class="summary-row">
              <span>Total Paid:</span>
              <span style="color: #28a745;">£${totalPaid.toFixed(2)}</span>
            </div>
            <div class="summary-row summary-total">
              <span>Balance Due:</span>
              <span style="color: ${totalDue > 0 ? '#dc3545' : '#28a745'};">£${totalDue.toFixed(2)}</span>
            </div>
          </div>
          
          <div class="footer">
            <p>Generated on ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            <p><strong>Thank you for your business!</strong></p>
            <p>${companyInfo.name}<br>
            ${companyInfo.address}<br>
            Tel: ${companyInfo.phone} | Email: ${companyInfo.email}</p>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handlePrintStatement = () => {
    const htmlContent = generateStatementHTML();
    if (!htmlContent) return;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url);
    printWindow.onload = () => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
        URL.revokeObjectURL(url);
      }, 500);
    };
  };

  const handleWhatsAppStatement = () => {
    const customer = customers.find(c => c.id === statementData.customer_id);
    if (!customer) return;

    const filteredSales = statementSales.filter(sale => {
      const saleDateStr = sale.date.split('T')[0];
      return saleDateStr >= statementData.start_date && saleDateStr <= statementData.end_date;
    });

    const totalAmount = filteredSales.reduce((sum, sale) => sum + sale.total, 0);
    const totalPaid = filteredSales.reduce((sum, sale) => sum + (sale.paid_amount || (sale.payment_status === 'paid' ? sale.total : 0)), 0);
    const totalDue = totalAmount - totalPaid;

    let phone = customer.phone || '';
    phone = phone.replace(/[\s\-\(\)]/g, '');
    if (!phone.startsWith('+')) {
      phone = '+44' + phone.replace(/^0/, '');
    }
    phone = phone.replace('+', '');

    const message = `
*ACCOUNT STATEMENT*
*${companyInfo.name}*
━━━━━━━━━━━━━━━━━━━━

Customer: ${customer.name}${customer.phone ? `
Phone: ${customer.phone}` : ''}${customer.address ? `
Address: ${customer.address}` : ''}
Period: ${statementData.start_date.split('-').reverse().join('/')} - ${statementData.end_date.split('-').reverse().join('/')}

*Invoices:*
${filteredSales.map(sale => `• ${sale.invoice_number} - £${sale.total.toFixed(2)} (${sale.payment_status})`).join('\n')}

━━━━━━━━━━━━━━━━━━━━
Total Amount: £${totalAmount.toFixed(2)}
Total Paid: £${totalPaid.toFixed(2)}
*Balance Due: £${totalDue.toFixed(2)}*
━━━━━━━━━━━━━━━━━━━━

Thank you for your business!

_${companyInfo.name}_
📍 ${companyInfo.address}
📞 ${companyInfo.phone}
✉️ ${companyInfo.email}
    `.trim();

    const whatsappUrl = phone 
      ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    
    // Try to open in new tab, fallback to same window
    const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = whatsappUrl;
    }
  };

  // Backend already paginates + filters + sorts by date desc.
  const filteredSales = sales;

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl" data-testid="sales-title">{t('sales')}</h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'إدارة المبيعات والفواتير' : 'Manage sales and invoices'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => openStatementDialog()} data-testid="generate-statement-btn">
            <FileText size={18} className="me-2" />
            {language === 'ar' ? 'كشف حساب' : 'Statement'}
          </Button>
          <ExportButton
            endpoint="sales"
            filename="sales"
            label={language === 'ar' ? 'تصدير Excel' : 'Export Excel'}
          />
          <Button onClick={() => setDialogOpen(true)} data-testid="add-sale-btn">
            <Plus size={18} className="me-2" />
            {t('newSale')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-border/40">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{language === 'ar' ? 'إجمالي المبيعات' : 'Total Sales'}</p>
            <p className="text-2xl font-bold text-primary" data-testid="sales-total-revenue">
              {formatCurrency(summary.total_revenue)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-border/40">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{language === 'ar' ? 'عدد الفواتير' : 'Total Invoices'}</p>
            <p className="text-2xl font-bold text-blue-600" data-testid="sales-total-count">{summary.total_count}</p>
          </CardContent>
        </Card>
        <Card className="border-border/40">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{language === 'ar' ? 'المعلقة' : 'Pending'}</p>
            <p className="text-2xl font-bold text-yellow-600" data-testid="sales-total-unpaid">
              {formatCurrency(summary.total_unpaid)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="border-border/40">
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder={language === 'ar' ? 'البحث بالعميل أو رقم الفاتورة...' : 'Search by customer or invoice number...'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="ps-10"
              data-testid="sales-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: language === 'ar' ? 'الكل' : 'All', style: 'bg-gray-100 text-gray-800 hover:bg-gray-200' },
              { key: 'paid', label: language === 'ar' ? 'مدفوع' : 'Paid', style: 'bg-green-100 text-green-800 hover:bg-green-200' },
              { key: 'partial', label: language === 'ar' ? 'جزئي' : 'Partial', style: 'bg-orange-100 text-orange-800 hover:bg-orange-200' },
              { key: 'pending', label: language === 'ar' ? 'غير مدفوع' : 'Pending', style: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' },
            ].map((f) => (
              <Button
                key={f.key}
                variant="ghost"
                size="sm"
                onClick={() => setStatusFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`rounded-full px-4 text-xs font-medium transition-all ${
                  statusFilter === f.key
                    ? `${f.style} ring-2 ring-offset-1 ring-primary`
                    : `${f.style} opacity-60`
                }`}
              >
                {f.label}
                {f.key !== 'all' && (
                  <span className="ms-1.5 font-bold">
                    {summary[`${f.key}_count`] || 0}
                  </span>
                )}
                {f.key === 'all' && (
                  <span className="ms-1.5 font-bold">{summary.total_count}</span>
                )}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sales Table */}
      <Card className="border-border/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredSales.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt size={48} className="mx-auto mb-2 opacity-50" />
              <p>{t('noData')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{language === 'ar' ? 'رقم الفاتورة' : 'Invoice #'}</TableHead>
                    <TableHead>{t('customerName')}</TableHead>
                    <TableHead>{t('date')}</TableHead>
                    <TableHead>{t('total')}</TableHead>
                    <TableHead>{language === 'ar' ? 'المدفوع' : 'Paid'}</TableHead>
                    <TableHead>{language === 'ar' ? 'المتبقي' : 'Remaining'}</TableHead>
                    <TableHead>{t('status')}</TableHead>
                    <TableHead className="text-end">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSales.map((sale) => (
                    <TableRow key={sale.id} data-testid={`sale-row-${sale.id}`}>
                      <TableCell className="font-mono text-sm">{sale.invoice_number}</TableCell>
                      <TableCell className="font-medium">{sale.customer_name}</TableCell>
                      <TableCell>{formatDate(sale.date)}</TableCell>
                      <TableCell className="font-bold text-primary">{formatCurrency(sale.total)}</TableCell>
                      <TableCell className="text-green-600">{formatCurrency(sale.payment_status === 'paid' ? sale.total : sale.paid_amount)}</TableCell>
                      <TableCell className={`font-bold ${(sale.total - (sale.payment_status === 'paid' ? sale.total : sale.paid_amount)) > 0 ? 'text-red-500' : 'text-green-600'}`}>
                        {formatCurrency(sale.total - (sale.payment_status === 'paid' ? sale.total : sale.paid_amount))}
                      </TableCell>
                      <TableCell>{getStatusBadge(sale.payment_status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => viewInvoice(sale)}
                            title={language === 'ar' ? 'عرض الفاتورة' : 'View Invoice'}
                            data-testid={`view-sale-${sale.id}`}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(sale)}
                            title={language === 'ar' ? 'تعديل الفاتورة' : 'Edit Invoice'}
                            data-testid={`edit-sale-${sale.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleWhatsAppSend(sale)}
                            title={language === 'ar' ? 'إرسال عبر واتساب' : 'Send via WhatsApp'}
                            className="text-green-600 hover:text-green-700"
                            data-testid={`whatsapp-sale-${sale.id}`}
                          >
                            <MessageCircle size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(sale.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-sale-${sale.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
        {/* Pagination footer */}
        {!loading && filteredTotal > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border/40 text-sm" data-testid="sales-pagination">
            <div className="text-muted-foreground" data-testid="pagination-info">
              {language === 'ar'
                ? `عرض ${page * PAGE_SIZE + 1} - ${Math.min((page + 1) * PAGE_SIZE, filteredTotal)} من ${filteredTotal}`
                : `Showing ${page * PAGE_SIZE + 1} - ${Math.min((page + 1) * PAGE_SIZE, filteredTotal)} of ${filteredTotal}`}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="pagination-prev"
              >
                {language === 'ar' ? 'السابق' : 'Previous'}
              </Button>
              <span className="text-muted-foreground px-2" data-testid="pagination-page">
                {language === 'ar'
                  ? `${page + 1} / ${Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE))}`
                  : `${page + 1} / ${Math.max(1, Math.ceil(filteredTotal / PAGE_SIZE))}`}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= filteredTotal}
                data-testid="pagination-next"
              >
                {language === 'ar' ? 'التالي' : 'Next'}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* New Sale Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingSale
                ? (language === 'ar' ? 'تعديل الفاتورة' : 'Edit Invoice')
                : t('newSale')
              }
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Mode Toggle - only for new sales */}
            {!editingSale && (
              <div className="flex gap-2 p-1 bg-muted rounded-lg">
                <Button
                  type="button"
                  variant={!quickMode ? 'default' : 'ghost'}
                  size="sm"
                  className="flex-1"
                  onClick={() => setQuickMode(false)}
                  data-testid="mode-products"
                >
                  {language === 'ar' ? 'فاتورة بمنتجات' : 'With Products'}
                </Button>
                <Button
                  type="button"
                  variant={quickMode ? 'default' : 'ghost'}
                  size="sm"
                  className="flex-1"
                  onClick={() => setQuickMode(true)}
                  data-testid="mode-quick"
                >
                  {language === 'ar' ? 'فاتورة سريعة (مبلغ فقط)' : 'Quick Invoice (Amount Only)'}
                </Button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('customerName')}</Label>
                <Select value={formData.customer_id || 'walk-in'} onValueChange={handleCustomerChange}>
                  <SelectTrigger data-testid="sale-customer-select">
                    <SelectValue placeholder={language === 'ar' ? 'اختر العميل' : 'Select customer'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="walk-in">{language === 'ar' ? 'عميل عابر' : 'Walk-in Customer'}</SelectItem>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('paymentStatus')}</Label>
                <Select
                  value={formData.payment_status}
                  onValueChange={(value) => setFormData({ ...formData, payment_status: value })}
                >
                  <SelectTrigger data-testid="sale-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">PAID</SelectItem>
                    <SelectItem value="partial">PARTIAL</SelectItem>
                    <SelectItem value="pending">PENDING</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'التاريخ' : 'Date'}</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                data-testid="sale-date-input"
              />
            </div>

            {/* Quick Amount Mode */}
            {quickMode && !editingSale && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <Label className="text-base font-semibold">{language === 'ar' ? 'المبلغ' : 'Amount'}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={language === 'ar' ? 'أدخل المبلغ...' : 'Enter amount...'}
                  value={quickAmount}
                  onChange={(e) => setQuickAmount(e.target.value)}
                  className="text-xl h-14 font-bold text-center"
                  data-testid="quick-amount-input"
                  autoFocus
                />
                <div className="flex justify-between items-center">
                  <span>{language === 'ar' ? 'تكلفة التوصيل' : 'Delivery Fee'}:</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.delivery_fee}
                    onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })}
                    className="w-32 h-8"
                    data-testid="quick-delivery-fee"
                  />
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>{language === 'ar' ? 'الإجمالي' : 'Total'}:</span>
                  <span className="text-primary">{formatCurrency(parseFloat(quickAmount || 0) + parseFloat(formData.delivery_fee || 0))}</span>
                </div>
              </div>
            )}

            {/* Add Item - Products mode */}
            {!quickMode && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <Label>{language === 'ar' ? 'إضافة منتج' : 'Add Product'}</Label>
              <div className="grid grid-cols-3 gap-2">
                <Select value={newItem.product_id} onValueChange={(v) => setNewItem({ ...newItem, product_id: v })}>
                  <SelectTrigger className="col-span-2" data-testid="sale-product-select">
                    <SelectValue placeholder={t('products')} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        {product.name} - {formatCurrency(product.price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder={t('quantity')}
                    value={newItem.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                    data-testid="sale-quantity-input"
                  />
                  <Button type="button" onClick={handleAddItem} data-testid="sale-add-item-btn">
                    {t('add')}
                  </Button>
                </div>
              </div>
            </div>
            )}

            {/* Items List - Products mode */}
            {!quickMode && formData.items.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Unit Price</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={`${item.product_id}-${index}`}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell>{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(index)}
                            className="text-destructive"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="p-4 bg-muted/50 space-y-2">
                  <div className="flex justify-between">
                    <span>{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}:</span>
                    <span className="font-medium">{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>{language === 'ar' ? 'الخصم' : 'Discount'}:</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                      className="w-32 h-8"
                      data-testid="sale-discount-input"
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span>{language === 'ar' ? 'تكلفة التوصيل' : 'Delivery Fee'}:</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.delivery_fee}
                      onChange={(e) => setFormData({ ...formData, delivery_fee: e.target.value })}
                      className="w-32 h-8"
                      data-testid="sale-delivery-fee-input"
                    />
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>{language === 'ar' ? 'الإجمالي' : 'Total'}:</span>
                    <span className="text-primary">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>
            )}

            {formData.payment_status !== 'paid' && (
              <div className="space-y-2">
                <Label>Paid Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.paid_amount}
                  onChange={(e) => setFormData({ ...formData, paid_amount: e.target.value })}
                  data-testid="sale-paid-input"
                />
              </div>
            )}

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
              >
                <SelectTrigger data-testid="sale-payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{language === 'ar' ? '💵 كاش' : '💵 Cash'}</SelectItem>
                  <SelectItem value="bank">{language === 'ar' ? '🏦 بنك' : '🏦 Bank'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={!quickMode && formData.items.length === 0 || quickMode && !parseFloat(quickAmount)} data-testid="sale-submit-btn">
                {editingSale
                  ? (language === 'ar' ? 'تحديث' : 'Update')
                  : t('save')
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice View Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Receipt size={20} />
              Invoice
            </DialogTitle>
          </DialogHeader>
          
          {selectedSale && (
            <div ref={printRef} className="space-y-4">
              <div className="text-center border-b pb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_877cf3ae-79e1-4b85-af95-e3f38fcbc2fb/artifacts/imx8202j_909ff97a-29cc-4bc8-9a25-59a8865473f7-300x294.png" 
                  alt="Logo" 
                  className="w-14 h-14 mx-auto mb-2"
                />
                <h1 className="text-lg font-bold text-primary">{companyInfo.name}</h1>
                <p className="text-xs text-muted-foreground mt-1">
                  📍 {companyInfo.address}<br/>
                  📞 {companyInfo.phone} | ✉️ {companyInfo.email}
                </p>
              </div>
              
              <div className="space-y-1 text-sm">
                <p><strong>Invoice:</strong> {selectedSale.invoice_number}</p>
                <p><strong>Date:</strong> {formatDateTime(selectedSale.date)}</p>
                <p><strong>Customer:</strong> {selectedSale.customer_name}</p>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Item</TableHead>
                    <TableHead className="text-xs">Qty</TableHead>
                    <TableHead className="text-xs">Price</TableHead>
                    <TableHead className="text-xs">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedSale.items.map((item, index) => (
                    <TableRow key={`${item.product_id}-${index}`}>
                      <TableCell className="text-sm">{item.product_name}</TableCell>
                      <TableCell className="text-sm">{item.quantity}</TableCell>
                      <TableCell className="text-sm">{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell className="text-sm">{formatCurrency(item.quantity * item.unit_price)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(selectedSale.subtotal)}</span>
                </div>
                {selectedSale.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount:</span>
                    <span>-{formatCurrency(selectedSale.discount)}</span>
                  </div>
                )}
                {(selectedSale.delivery_fee || 0) > 0 && (
                  <div className="flex justify-between">
                    <span>{language === 'ar' ? 'تكلفة التوصيل' : 'Delivery Fee'}:</span>
                    <span>{formatCurrency(selectedSale.delivery_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>{language === 'ar' ? 'الإجمالي' : 'Total'}:</span>
                  <span>{formatCurrency(selectedSale.total)}</span>
                </div>
                {selectedSale.payment_status !== 'paid' && (
                  <>
                    <div className="flex justify-between text-green-600">
                      <span>{language === 'ar' ? 'المدفوع' : 'Paid'}:</span>
                      <span>{formatCurrency(selectedSale.paid_amount)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-red-500">
                      <span>{language === 'ar' ? 'المتبقي' : 'Remaining'}:</span>
                      <span>{formatCurrency(selectedSale.total - selectedSale.paid_amount)}</span>
                    </div>
                  </>
                )}
              </div>
              
              <div className="text-center text-xs text-muted-foreground pt-4 border-t">
                <p>Thank you for your business!</p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
              Close
            </Button>
            <Button 
              variant="outline" 
              onClick={() => selectedSale && handleWhatsAppSend(selectedSale)}
              className="text-green-600 border-green-600 hover:bg-green-50"
            >
              <MessageCircle size={16} className="me-2" />
              WhatsApp
            </Button>
            <Button onClick={handlePrint} data-testid="print-invoice-btn">
              <Printer size={16} className="me-2" />
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Statement Dialog */}
      <Dialog open={statementDialogOpen} onOpenChange={setStatementDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FileText size={20} />
              {language === 'ar' ? 'كشف حساب العميل' : 'Client Statement'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{language === 'ar' ? 'العميل' : 'Customer'}</Label>
              <Select 
                value={statementData.customer_id} 
                onValueChange={(v) => setStatementData({...statementData, customer_id: v})}
              >
                <SelectTrigger data-testid="statement-customer-select">
                  <SelectValue placeholder={language === 'ar' ? 'اختر العميل' : 'Select customer'} />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'من تاريخ' : 'From Date'}</Label>
                <Input
                  type="date"
                  value={statementData.start_date}
                  onChange={(e) => setStatementData({...statementData, start_date: e.target.value})}
                  data-testid="statement-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'ar' ? 'إلى تاريخ' : 'To Date'}</Label>
                <Input
                  type="date"
                  value={statementData.end_date}
                  onChange={(e) => setStatementData({...statementData, end_date: e.target.value})}
                  data-testid="statement-end-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'ar' ? 'نوع التقرير' : 'Report Type'}</Label>
              <Select 
                value={statementData.type} 
                onValueChange={(v) => setStatementData({...statementData, type: v})}
              >
                <SelectTrigger data-testid="statement-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">{language === 'ar' ? 'ملخص' : 'Summary'}</SelectItem>
                  <SelectItem value="detailed">{language === 'ar' ? 'تفصيلي' : 'Detailed'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setStatementDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleWhatsAppStatement}
              disabled={!statementData.customer_id}
              className="text-green-600 border-green-600 hover:bg-green-50"
            >
              <MessageCircle size={16} className="me-2" />
              WhatsApp
            </Button>
            <Button 
              onClick={handlePrintStatement} 
              disabled={!statementData.customer_id}
              data-testid="print-statement-btn"
            >
              <Printer size={16} className="me-2" />
              {language === 'ar' ? 'طباعة' : 'Print'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
